/**
 * The analysis registry — single source of truth (analysis-launcher spec D8)
 * for the layer context menu (P1), the launcher (P2) and the Analyses hub
 * (P5). Display order is the array order.
 *
 * `acceptsContext` restates the old flat context menu's conditions
 * (`Layers.tsx` before 2026-10-10): POINTCLOUD → deviation / ground / mesh /
 * clearance; any surface candidate → surface difference;
 * VECTOR → stockpile / rainfall; a registered crest source → crest line;
 * an IFC model → LandXML export; an IMAGE layer → IPRJ export. `registry.test.ts` pins that matrix.
 * Multi-layer thickness (`layerStack`) is deliberately absent — owner
 * amendment A1 turned it off as a run entry point everywhere; its dialog and
 * the result views of existing stacks remain.
 * Deliberate departure (D10, P4): deviation also accepts its reference
 * (design) layers — VECTOR and the deviation MODEL types.
 */
import {
  Box,
  CloudRain,
  Diff,
  FileDown,
  FileOutput,
  Files,
  FlaskConical,
  FolderArchive,
  Grid3x3,
  Mountain,
  Spline,
  TrainTrack,
  WandSparkles,
  type LucideIcon,
} from 'lucide-react';

import type { LayerData } from '@/atoms/layers.atom';
import { ProcessJobType } from '@/graphql/graphql';
import type { FeatureKey } from '@/lib/featureGates';

import {
  isClearanceCandidate,
  isDeviationReferenceCandidate,
  isIfcModelLayer,
  isImageLayer,
  isReadyLandxmlMergeSource,
  isPointcloudLayer,
  isSurfaceCandidate,
  isVectorLayer,
} from './candidates';
import type { AnalysisDescriptor, AnalysisId, MenuGroup } from './types';

/**
 * Every PostHog flag an analysis can sit behind. `useAnalysisAvailability`
 * reads each one with its own top-level `useFeatureFlagEnabled` call (hooks
 * may not run in a loop), keyed by this union — adding a flag here without
 * reading it there is a compile error.
 */
export const ANALYSIS_FLAGS = [
  'deviationAnalysis',
  'surfaceDifference',
  'stockpileAnalysis',
  'rainfallAnalysis',
  'groundAnalysis',
  'meshExport',
  'crest',
] as const;
export type AnalysisFlag = (typeof ANALYSIS_FLAGS)[number];

export interface MenuGroupDescriptor {
  id: MenuGroup;
  labelKey: string;
  icon: LucideIcon;
}

/** The context menu's three submenus, in menu order. */
export const MENU_GROUPS: readonly MenuGroupDescriptor[] = [
  { id: 'analyse', labelKey: 'analysisLauncher.menu.analyse', icon: FlaskConical },
  { id: 'generate', labelKey: 'analysisLauncher.menu.generate', icon: WandSparkles },
  { id: 'export', labelKey: 'analysisLauncher.menu.export', icon: FileDown },
];

type BlockedReason =
  | 'needsPointcloud'
  | 'needsSurface'
  | 'needsVector'
  | 'needsIfcModel'
  | 'needsCrestSource'
  | 'needsLandxml'
  | 'needsImageLayer';

const needs = (satisfied: boolean, reason: BlockedReason) =>
  satisfied ? null : { reasonKey: `analysisLauncher.blocked.${reason}` };

const i18nKeys = (id: AnalysisId) => ({
  titleKey: `analysisLauncher.items.${id}.title`,
  subtitleKey: `analysisLauncher.items.${id}.subtitle`,
  menuLabelKey: `analysisLauncher.items.${id}.menu`,
});

/** Narrows `flag` to a known PostHog flag at compile time. */
const define = (d: AnalysisDescriptor & { flag: AnalysisFlag | readonly AnalysisFlag[] | null }): AnalysisDescriptor => d;

export const ANALYSES: readonly AnalysisDescriptor[] = [
  define({
    id: 'compare',
    category: 'compare',
    mode: 'analyse',
    menuGroup: 'analyse',
    ...i18nKeys('compare'),
    icon: Diff,
    gate: 'analysis.surfaceDifference',
    // Either engine's flag shows the tool; the dialog hides the result card
    // whose own flag is off.
    flag: ['surfaceDifference', 'deviationAnalysis'],
    acceptsContext: (layer) => isSurfaceCandidate(layer) || isDeviationReferenceCandidate(layer),
    prerequisite: (ctx) => needs(ctx.layers.some(isSurfaceCandidate), 'needsSurface'),
    jobTypes: [ProcessJobType.SurfaceDifference, ProcessJobType.Deviation],
  }),
  define({
    id: 'stockpile',
    category: 'volumes',
    mode: 'analyse',
    menuGroup: 'analyse',
    ...i18nKeys('stockpile'),
    icon: Mountain,
    // Context-dependent (I1): seeding the compute from a layer's polygons is
    // the PRO from-surface feature; the no-layer launch enters draw mode,
    // which is the free toolbar tool (`Viewer.tsx` gates it the same way).
    gate: 'viewer.stockpileFromSurface',
    gateWithoutContext: 'viewer.stockpile',
    flag: 'stockpileAnalysis',
    acceptsContext: (layer) => isVectorLayer(layer),
    prerequisite: (ctx) => needs(ctx.layers.some(isPointcloudLayer), 'needsPointcloud'),
    jobTypes: [],
  }),
  define({
    id: 'clearance',
    category: 'check',
    mode: 'analyse',
    menuGroup: 'analyse',
    ...i18nKeys('clearance'),
    icon: TrainTrack,
    gate: 'analysis.clearance',
    // Ruling R10: no PostHog flag — subscription gate only.
    flag: null,
    acceptsContext: (layer) => isClearanceCandidate(layer),
    // Deliberately NOT gated on `hasAlignments`: the dialog can load
    // alignments from LandXML layers the viewer has not loaded yet.
    prerequisite: (ctx) => needs(ctx.layers.some(isClearanceCandidate), 'needsPointcloud'),
    jobTypes: [ProcessJobType.Clearance],
  }),
  define({
    id: 'rainfall',
    category: 'check',
    mode: 'analyse',
    menuGroup: 'analyse',
    ...i18nKeys('rainfall'),
    icon: CloudRain,
    // D7: defined since the gate map existed but never checked until now.
    gate: 'analysis.rainfall',
    flag: 'rainfallAnalysis',
    acceptsContext: (layer) => isVectorLayer(layer),
    prerequisite: (ctx) => needs(ctx.layers.some(isVectorLayer), 'needsVector'),
    jobTypes: [ProcessJobType.RainfallAnalysis],
  }),
  define({
    id: 'groundClassify',
    category: 'generate',
    mode: 'generate',
    menuGroup: 'generate',
    ...i18nKeys('groundClassify'),
    icon: Grid3x3,
    gate: 'analysis.groundClassify',
    flag: 'groundAnalysis',
    acceptsContext: (layer) => isPointcloudLayer(layer),
    prerequisite: (ctx) => needs(ctx.layers.some(isPointcloudLayer), 'needsPointcloud'),
    jobTypes: [ProcessJobType.GroundClassify],
  }),
  define({
    id: 'mesh',
    category: 'generate',
    mode: 'generate',
    menuGroup: 'generate',
    ...i18nKeys('mesh'),
    icon: Box,
    gate: 'analysis.meshExport',
    flag: 'meshExport',
    acceptsContext: (layer) => isPointcloudLayer(layer),
    prerequisite: (ctx) => needs(ctx.layers.some(isPointcloudLayer), 'needsPointcloud'),
    jobTypes: [ProcessJobType.Mesh],
  }),
  define({
    id: 'crestLine',
    category: 'generate',
    mode: 'generate',
    menuGroup: 'generate',
    ...i18nKeys('crestLine'),
    icon: Spline,
    gate: 'analysis.crestLine',
    flag: 'crest',
    // Only layers registered in crestSourcesAtom (TIN + identity transform):
    // the sampler reads raw file coordinates, anything else has nothing to
    // extract from.
    acceptsContext: (layer, ctx) => ctx.crestSources.has(layer.id),
    prerequisite: (ctx) => needs(ctx.crestSources.size > 0, 'needsCrestSource'),
    jobTypes: [],
  }),
  define({
    id: 'landxmlExport',
    category: 'export',
    mode: 'generate',
    menuGroup: 'export',
    ...i18nKeys('landxmlExport'),
    icon: FileOutput,
    gate: 'export.landxml',
    flag: null,
    acceptsContext: (layer) => isIfcModelLayer(layer),
    prerequisite: (ctx) => needs(ctx.layers.some((l) => isIfcModelLayer(l)), 'needsIfcModel'),
    jobTypes: [ProcessJobType.LandxmlExport],
  }),
  define({
    id: 'landxmlMerge',
    category: 'export',
    mode: 'generate',
    menuGroup: 'export',
    ...i18nKeys('landxmlMerge'),
    icon: Files,
    gate: null,
    flag: null,
    acceptsContext: (layer) => isReadyLandxmlMergeSource(layer),
    prerequisite: (ctx) => needs(ctx.layers.filter(isReadyLandxmlMergeSource).length >= 2, 'needsLandxml'),
    jobTypes: [ProcessJobType.LandxmlMerge],
  }),
  define({
    id: 'iprjExport',
    category: 'export',
    mode: 'generate',
    menuGroup: 'export',
    ...i18nKeys('iprjExport'),
    icon: FolderArchive,
    gate: 'export.iprj',
    flag: null,
    acceptsContext: (layer) => isImageLayer(layer),
    prerequisite: (ctx) => needs(ctx.layers.some(isImageLayer), 'needsImageLayer'),
    jobTypes: [ProcessJobType.IprjExport],
  }),
];

const BY_ID = new Map<AnalysisId, AnalysisDescriptor>(ANALYSES.map((d) => [d.id, d]));

export function getAnalysis(id: AnalysisId): AnalysisDescriptor {
  const descriptor = BY_ID.get(id);
  if (!descriptor) throw new Error(`Unknown analysis id: ${id}`);
  return descriptor;
}

/** The subscription gate for launching `id` with (or without) a context layer.
 *  Availability and the click-time check in `useLaunchAnalysis` both go
 *  through this, so they cannot disagree. */
export function analysisGate(id: AnalysisId, contextLayer?: LayerData): FeatureKey | null {
  const d = getAnalysis(id);
  return contextLayer ? d.gate : (d.gateWithoutContext ?? d.gate);
}
